import React from 'react';
import {
  Box,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';
import ThunderstormRoundedIcon from '@mui/icons-material/ThunderstormRounded';
import GrainRoundedIcon from '@mui/icons-material/GrainRounded';
import AcUnitRoundedIcon from '@mui/icons-material/AcUnitRounded';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import { WeatherForecastDay, WeatherForecastHour, WeatherReport } from '../../types/weather';
import { alpha } from '@mui/material/styles';
import { createLogger } from '../../utils/logger';
import { textScale } from '../../theme/standards';

const log = createLogger('WeatherSummaryCard');

interface WeatherSummaryCardProps {
  weather: WeatherReport | null;
  isLoading: boolean;
}

const formatTemperatureLabel = (temperatureC?: number | null, temperatureF?: number | null): string => {
  const hasCelsius = typeof temperatureC === 'number' && !Number.isNaN(temperatureC);
  const hasFahrenheit = typeof temperatureF === 'number' && !Number.isNaN(temperatureF);

  if (hasCelsius && hasFahrenheit) {
    return `${Math.round(temperatureC!)}°C / ${Math.round(temperatureF!)}°F`;
  }
  if (hasCelsius) {
    return `${Math.round(temperatureC!)}°C`;
  }
  if (hasFahrenheit) {
    return `${Math.round(temperatureF!)}°F`;
  }
  return '–';
};

const pickConditionIcon = (code?: string | null, fallback?: string | null) => {
  const source = (code || fallback || '').toLowerCase();
  if (source.includes('thunder')) {
    return <ThunderstormRoundedIcon />;
  }
  if (source.includes('rain') || source.includes('shower') || source.includes('drizzle')) {
    return <GrainRoundedIcon />;
  }
  if (source.includes('snow')) {
    return <AcUnitRoundedIcon />;
  }
  if (source.includes('cloud')) {
    return <CloudQueueRoundedIcon />;
  }
  if (source.includes('wind')) {
    return <AirRoundedIcon />;
  }
  return <WbSunnyRoundedIcon />;
};

const formatDateLabel = (date?: string | null): string => {
  if (!date) {
    return 'Upcoming';
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return 'Upcoming';
  }

  // Format as "Wed, Oct 30"
  return new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' }).format(parsed);
};

const formatTimeLabel = (timestamp?: string | null, timeZone?: string | null): string | null => {
  if (!timestamp) {
    return null;
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  try {
    // Skip invalid timezone formats like "UTC+1" and use local time instead
    const isValidTimezone = timeZone && !timeZone.startsWith('UTC');
    const formatter = new Intl.DateTimeFormat('en', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: isValidTimezone ? timeZone : undefined,
    });
    return formatter.format(parsed);
  } catch (error) {
    log.error('Failed to format weather timestamp', error);
    return parsed.toLocaleTimeString();
  }
};

const formatHourLabel = (timestamp?: string | null, timeZone?: string | null): string => {
  if (!timestamp) {
    return '–';
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return '–';
  }

  try {
    const isValidTimezone = timeZone && !timeZone.startsWith('UTC');
    const formatter = new Intl.DateTimeFormat('en', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: isValidTimezone ? timeZone : undefined,
    });
    return formatter.format(parsed);
  } catch (error) {
    log.error('Failed to format hourly timestamp', error);
    return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
};

const pickNumericValue = (values: Array<number | null | undefined>): number | null => {
  for (const value of values) {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
  }
  return null;
};

const formatHighTemperature = (forecast: WeatherForecastDay): string => {
  const celsius = pickNumericValue([forecast.temperatureMaxC, forecast.highC]);
  if (celsius !== null) {
    return `${Math.round(celsius)}°`;
  }
  const fahrenheit = pickNumericValue([forecast.temperatureMaxF, forecast.highF]);
  return fahrenheit !== null ? `${Math.round(fahrenheit)}°` : '–';
};

const formatLowTemperature = (forecast: WeatherForecastDay): string => {
  const celsius = pickNumericValue([forecast.temperatureMinC, forecast.lowC]);
  if (celsius !== null) {
    return `${Math.round(celsius)}°`;
  }
  const fahrenheit = pickNumericValue([forecast.temperatureMinF, forecast.lowF]);
  return fahrenheit !== null ? `${Math.round(fahrenheit)}°` : '–';
};

const formatPrecipitationLabel = (forecast: WeatherForecastDay): string => (
  typeof forecast.precipitationChance === 'number'
    ? `${forecast.precipitationChance}%`
    : '–'
);

const formatSingleTemperature = (temperatureC?: number | null, temperatureF?: number | null): string => {
  if (typeof temperatureC === 'number' && !Number.isNaN(temperatureC)) {
    return `${Math.round(temperatureC)}°`;
  }
  if (typeof temperatureF === 'number' && !Number.isNaN(temperatureF)) {
    return `${Math.round(temperatureF)}°`;
  }
  return '–';
};

interface ForecastRowProps {
  forecast: WeatherForecastDay;
  isSelected?: boolean;
  onSelect?: () => void;
}

const ForecastRow: React.FC<ForecastRowProps> = ({ forecast, isSelected = false, onSelect }) => {
  const dayLabel = formatDateLabel(forecast.date);
  const icon = pickConditionIcon(forecast.conditionCode, forecast.conditionText);
  const highLabel = formatHighTemperature(forecast);
  const lowLabel = formatLowTemperature(forecast);
  const precipLabel = formatPrecipitationLabel(forecast);

  const handleClick = () => {
    onSelect?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!onSelect) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: { xs: 0.5, sm: 0.75 },
        borderRadius: { xs: 1, sm: 1.5 },
        px: { xs: 0.75, sm: 1 },
        cursor: onSelect ? 'pointer' : 'default',
        border: (theme) => isSelected
          ? `1px solid ${alpha(theme.palette.primary.main, 0.32)}`
          : '1px solid transparent',
        backgroundColor: (theme) => isSelected
          ? alpha(theme.palette.primary.main, 0.12)
          : 'transparent',
        transition: 'background-color 0.2s ease, border 0.2s ease',
        '&:hover': onSelect
          ? {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }
          : undefined,
      }}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-pressed={onSelect ? isSelected : undefined}
      onClick={onSelect ? handleClick : undefined}
      onKeyDown={onSelect ? handleKeyDown : undefined}
    >
      <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 }, flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>
            {dayLabel}
          </Typography>
          {forecast.conditionText && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              {forecast.conditionText}
            </Typography>
          )}
        </Box>
      </Stack>
      
      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {highLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {lowLabel}
          </Typography>
        </Stack>
        <Chip 
          label={`☔ ${precipLabel}`} 
          size="small" 
          sx={{ 
            fontSize: { xs: '0.65rem', sm: textScale.support.fontSize },
            height: { xs: 20, sm: 22 }, 
            px: { xs: 0.75, sm: 1 },
            '& .MuiChip-label': {
              px: 0.5,
            },
          }} 
        />
      </Stack>
    </Stack>
  );
};

const HourlyForecastCell: React.FC<{ entry: WeatherForecastHour; timeZone?: string | null }> = ({ entry, timeZone }) => {
  const icon = pickConditionIcon(entry.conditionCode, entry.conditionText);
  const timeLabel = formatHourLabel(entry.timestamp, timeZone);
  const temperatureLabel = formatSingleTemperature(entry.temperatureC, entry.temperatureF);
  const precipLabel = typeof entry.precipitationChance === 'number' && !Number.isNaN(entry.precipitationChance)
    ? `${Math.round(entry.precipitationChance)}%`
    : '–';

  return (
    <Stack
      spacing={0.4}
      alignItems="center"
      sx={{
        minWidth: { xs: 72, sm: 90 },
        borderRadius: 2,
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
        py: { xs: 0.75, sm: 1 },
        px: { xs: 0.75, sm: 1 },
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
        {timeLabel}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 } }}>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
        {temperatureLabel}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
        ☔ {precipLabel}
      </Typography>
    </Stack>
  );
};

interface HourlyForecastStripProps {
  entries: WeatherForecastHour[];
  dayLabel: string;
  timeZone?: string | null;
}

const HourlyForecastStrip: React.FC<HourlyForecastStripProps> = ({ entries, dayLabel, timeZone }) => {
  const hourlyEntries = React.useMemo(
    () => (entries ?? []).filter((entry) => entry).slice(0, 8),
    [entries],
  );

  if (hourlyEntries.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1,
        px: { xs: 0.75, md: 1.5 },
        py: { xs: 0.75, md: 1.25 },
        borderRadius: { xs: 1.5, md: 2 },
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.08),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 0.75, md: 1 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
          Hourly outlook · {dayLabel}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
          Next ~{hourlyEntries.length * 3}h
        </Typography>
      </Stack>
      <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} sx={{ overflowX: 'auto', pb: 0.25 }}>
        {hourlyEntries.map((entry, index) => (
          <HourlyForecastCell
            key={entry.timestamp || `${dayLabel}-${index}`}
            entry={entry}
            timeZone={timeZone}
          />
        ))}
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
        Tap the same day again to hide the hourly view.
      </Typography>
    </Box>
  );
};

export const WeatherSummaryCard: React.FC<WeatherSummaryCardProps> = ({ weather, isLoading }) => {
  const [selectedDayIndex, setSelectedDayIndex] = React.useState<number | null>(null);
  const forecastSlice = weather?.dailyForecasts?.slice(0, 4) ?? [];
  const selectedForecast = selectedDayIndex !== null ? forecastSlice[selectedDayIndex] : null;
  const selectedDayLabel = selectedForecast ? formatDateLabel(selectedForecast.date) : null;
  const selectedHourlyEntries = selectedForecast?.hourlyForecasts ?? [];

  React.useEffect(() => {
    if (selectedDayIndex !== null && selectedDayIndex >= forecastSlice.length) {
      setSelectedDayIndex(null);
    }
  }, [forecastSlice.length, selectedDayIndex]);

  React.useEffect(() => {
    setSelectedDayIndex(null);
  }, [weather?.resolvedLocation?.locationId, weather?.resolvedLocation?.query]);

  const current = weather?.currentConditions;
  const locationLabel = weather?.resolvedLocation?.displayName || weather?.resolvedLocation?.query || 'Destination weather';
  const updatedLabel = formatTimeLabel(current?.observedAt, weather?.resolvedLocation?.timeZone ?? undefined);

  if (isLoading && !weather) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Checking the forecast…
          </Typography>
          <LinearProgress />
        </Stack>
      </Paper>
    );
  }

  if (!weather) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
        <Stack spacing={0.75}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Weather outlook
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            We couldn't load the weather right now. Try again later or refresh the page once you're online.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const temperatureLabel = formatTemperatureLabel(current?.temperatureC, current?.temperatureF);
  const conditionIcon = pickConditionIcon(current?.conditionCode, current?.conditionText);
  const handleForecastSelect = (index: number) => {
    setSelectedDayIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: { xs: 2.5, md: 3 },
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        background: (theme) => alpha(theme.palette.primary.light, 0.08),
        backdropFilter: 'blur(6px)',
      }}
    >
      <Stack spacing={{ xs: 0.5, sm: 0.75 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            wordBreak: 'break-word',
            mb: 0.25,
            pl: 0.5,
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
          }}
        >
          {locationLabel}
        </Typography>
        {selectedForecast ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', pl: 0.5, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
            Hourly forecast for {selectedDayLabel ?? 'selected day'}. Tap the day again to hide.
          </Typography>
        ) : updatedLabel ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', pl: 0.5, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
            Updated {updatedLabel}
          </Typography>
        ) : null}

        <Stack
          direction="row"
          spacing={{ xs: 0.75, sm: 1.5 }}
          alignItems="center"
          sx={{
            mb: { xs: 0.25, sm: 0.5 },
            pl: 0.5,
            flexWrap: 'wrap',
            rowGap: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              flexShrink: 0,
            }}
          >
            {conditionIcon}
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {temperatureLabel}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexShrink: 0, ml: { xs: 0, sm: 1.5 } }}
          >
            {typeof current?.humidity === 'number' && (
              <Chip 
                label={`${current.humidity}%`} 
                size="small" 
                sx={{ 
                  fontSize: { xs: '0.65rem', sm: textScale.support.fontSize }, 
                  height: { xs: 20, sm: 24 }, 
                  px: { xs: 0.75, sm: 1 },
                  '& .MuiChip-label': {
                    px: 0.5,
                  },
                }} 
              />
            )}
            {typeof current?.windSpeedKph === 'number' && (
              <Chip 
                label={`${Math.round(current.windSpeedKph)}km/h`} 
                size="small" 
                sx={{ 
                  fontSize: { xs: '0.65rem', sm: textScale.support.fontSize }, 
                  height: { xs: 20, sm: 24 }, 
                  px: { xs: 0.75, sm: 1 },
                  '& .MuiChip-label': {
                    px: 0.5,
                  },
                }} 
              />
            )}
          </Stack>
        </Stack>

        {selectedForecast && (
          <HourlyForecastStrip
            entries={selectedHourlyEntries}
            dayLabel={selectedDayLabel ?? 'Selected day'}
            timeZone={weather?.resolvedLocation?.timeZone ?? null}
          />
        )}

        {forecastSlice.length > 0 && (
          <>
            <Divider sx={{ my: { xs: 0.75, sm: 1 } }} />
            <Stack spacing={0.5}>
              {forecastSlice.map((entry, index) => (
                <ForecastRow
                  key={entry.date || index}
                  forecast={entry}
                  isSelected={selectedDayIndex === index}
                  onSelect={() => handleForecastSelect(index)}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
